import { canApprove, lei, roDateTime, type PurchaseOrder, type Rfq, t } from '@procumi/shared';
import { useState } from 'react';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { currentUser } from '../lib/me';

/**
 * Aprobarea pe pagina comenzii: pașii, cine a decis și ce a scris, plus
 * butoanele „Aprobă” / „Respinge” pentru cine decide pasul de acum. Nu se
 * tipărește: documentul comenzii rămâne curat.
 */
export function ApprovalPanel({
  rfqId,
  order,
  toast,
  onChange,
  onCompare,
  splitTotal,
}: {
  rfqId: string;
  order: PurchaseOrder;
  toast: Toast;
  onChange: (rfq: Rfq) => void;
  onCompare: () => void;
  /** comanda e o parte dintr-o comandă împărțită: totalul ei, cu care trece pragurile */
  splitTotal?: number | null;
}) {
  const steps = order.approvals ?? [];
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  if (!steps.length) return null;

  const mine = canApprove(order, currentUser());
  const decide = async (approve: boolean) => {
    if (!approve && !comment.trim()) {
      toast(t('Scrie de ce respingi comanda: omul care a cerut-o primește motivul.'));
      return;
    }
    setBusy(true);
    try {
      onChange(
        approve
          ? await api.approveOrder(rfqId, order.id, comment.trim())
          : await api.rejectOrder(rfqId, order.id, comment.trim()),
      );
      setComment('');
      toast(approve ? t('Ai aprobat comanda {0}', order.number) : t('Ai respins comanda {0}', order.number));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const head =
    order.status === 'pending'
      ? t('Așteaptă aprobare')
      : order.status === 'rejected'
        ? t('Respinsă la aprobare')
        : t('Aprobată');

  return (
    <section className={`sheet pad appr-panel no-print ${order.status}`} aria-labelledby="appr-panel-t">
      <div className="appr-panel-head">
        <h2 id="appr-panel-t">{head}</h2>
        <button className="link" type="button" onClick={onCompare}>
          {t('Vezi comparația ofertelor')}
        </button>
      </div>
      {order.status === 'pending' ? (
        <p className="hint" style={{ margin: 0 }}>
          {t(
            'Comanda nu pleacă la furnizor până nu trece de toți pașii. Valoarea: {0} fără TVA.{1}',
            lei(order.netLei),
            splitTotal != null && splitTotal > order.netLei
              ? t(
                  ' E o parte dintr-o comandă împărțită de {0}: pragurile se socotesc pe toată împărțirea.',
                  lei(splitTotal),
                )
              : '',
          )}
        </p>
      ) : null}
      <ol className="appr-trail">
        {steps.map((s) => (
          <li key={s.step} className={s.decision ?? (order.status === 'pending' ? 'now' : 'skip')}>
            <span className="appr-dot" aria-hidden="true" />
            <div>
              <b>
                {t('Peste {0}: {1}', lei(s.minValue), s.approver ? s.approver.name : t('oricare aprobator'))}
              </b>
              <span>
                {s.decision === 'approved'
                  ? t(
                      'Aprobată de {0}{1}',
                      s.decidedBy?.name ?? '—',
                      s.decidedAt ? `, ${roDateTime(s.decidedAt)}` : '',
                    )
                  : s.decision === 'rejected'
                    ? t(
                        'Respinsă de {0}{1}',
                        s.decidedBy?.name ?? '—',
                        s.decidedAt ? `, ${roDateTime(s.decidedAt)}` : '',
                      )
                    : order.status === 'pending'
                      ? t('Așteaptă decizia')
                      : t('Nu s-a mai ajuns aici')}
              </span>
              {s.comment ? <q>{s.comment}</q> : null}
            </div>
          </li>
        ))}
      </ol>
      {mine ? (
        <div className="appr-decide">
          <label className="f" htmlFor="appr-comment">
            {t('Notă (la respingere, obligatorie)')}
            <textarea
              id="appr-comment"
              className="inp"
              rows={2}
              value={comment}
              maxLength={500}
              placeholder={t('ex. Negociază transportul inclus și retrimite')}
              onChange={(e) => setComment(e.target.value)}
            />
          </label>
          <div className="appr-acts">
            <button className="btn" type="button" disabled={busy} onClick={() => void decide(false)}>
              {t('Respinge')}
            </button>
            <button className="btn primary" type="button" disabled={busy} onClick={() => void decide(true)}>
              {t('Aprobă comanda')}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
